import React, { useContext, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import RoomContext from "../../context/RoomContext";
import {
  buttonBitFromEvent,
  buttonsFromEvent,
  isBlockedHotkey,
  keyPayloadFromEvent,
  modsFromEvent,
  videoPointToNorm,
  wheelStepsFromEvent,
} from "../../helpers/remoteControl/protocol";

const CURSOR_SVG = encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path d="M4 3l12 8.5-5.2 1.2 3.4 7.3-2.4 1.1-3.4-7.3L4 18.5V3z"
      fill="#4C9AFF" stroke="#0B1F3A" stroke-width="1.2" stroke-linejoin="round"/>
  </svg>`
);

const RemoteControlSurface = ({ videoRef, hostPeerId }) => {
  const { roomClient } = useContext(RoomContext);
  const meId = useSelector((state) => state.me.id);
  const session = useSelector((state) => state.remoteControl);
  const active =
    session &&
    session.status === "active" &&
    session.controllerPeerId === meId &&
    session.hostPeerId === hostPeerId;
  const capturing = useRef(false);
  const hovered = useRef(false);
  const wheelAccumRef = useRef({ x: 0, y: 0 });
  const lastPointRef = useRef({ x: 0.5, y: 0.5, inside: false });
  const surfaceRef = useRef(null);
  const cursorRef = useRef(null);

  const setLocalCursor = (clientX, clientY, visible) => {
    const cursor = cursorRef.current;
    const surface = surfaceRef.current;
    if (!cursor || !surface) return;

    if (!visible) {
      cursor.style.opacity = "0";
      return;
    }

    const rect = surface.getBoundingClientRect();
    cursor.style.opacity = "1";
    cursor.style.transform = `translate(${clientX - rect.left}px, ${
      clientY - rect.top
    }px)`;
  };

  useEffect(() => {
    if (!active) return undefined;

    const onKey = (event, type) => {
      if (!hovered.current && !capturing.current) return;

      if (isBlockedHotkey(event)) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      roomClient.sendRemoteControlInput({
        t: type,
        k: keyPayloadFromEvent(event),
        mods: modsFromEvent(event),
      });
    };

    const down = (event) => onKey(event, "kd");
    const up = (event) => onKey(event, "ku");

    window.addEventListener("keydown", down, true);
    window.addEventListener("keyup", up, true);

    return () => {
      window.removeEventListener("keydown", down, true);
      window.removeEventListener("keyup", up, true);
    };
  }, [active, roomClient]);

  useEffect(() => {
    if (!active) {
      wheelAccumRef.current = { x: 0, y: 0 };
      lastPointRef.current = { x: 0.5, y: 0.5, inside: false };
      setLocalCursor(0, 0, false);
    }
  }, [active]);

  if (!active) return null;

  const pointFromEvent = (event) =>
    videoPointToNorm(videoRef && videoRef.current, event.clientX, event.clientY);

  const wheelPointFromEvent = (event) => {
    const point = pointFromEvent(event);

    if (point.inside) return point;

    if (lastPointRef.current.inside) return lastPointRef.current;

    return { x: point.x, y: point.y, inside: true };
  };

  const handleWheel = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const point = wheelPointFromEvent(event);
    const { dx, dy } = wheelStepsFromEvent(event, wheelAccumRef.current);

    if (!dx && !dy) return;

    roomClient.sendRemoteControlInput({
      t: "w",
      x: point.x,
      y: point.y,
      dx,
      dy,
    });
  };

  return (
    <div
      ref={surfaceRef}
      tabIndex={0}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 21,
        cursor: "none",
        outline: "none",
        touchAction: "none",
        boxShadow: "inset 0 0 0 1px rgba(76, 154, 255, 0.55)",
      }}
      onContextMenu={(event) => event.preventDefault()}
      onPointerEnter={() => {
        hovered.current = true;
      }}
      onPointerLeave={() => {
        if (!capturing.current) {
          hovered.current = false;
          setLocalCursor(0, 0, false);
        }
      }}
      onWheel={handleWheel}
      onPointerDown={(event) => {
        capturing.current = true;
        event.currentTarget.setPointerCapture(event.pointerId);
        event.currentTarget.focus();
        const point = pointFromEvent(event);
        setLocalCursor(event.clientX, event.clientY, point.inside);
        if (!point.inside) return;
        lastPointRef.current = point;
        roomClient.sendRemoteControlInput({
          t: "d",
          x: point.x,
          y: point.y,
          b: buttonBitFromEvent(event),
        });
      }}
      onPointerMove={(event) => {
        const point = pointFromEvent(event);
        if (point.inside) lastPointRef.current = point;
        setLocalCursor(
          event.clientX,
          event.clientY,
          point.inside || capturing.current
        );
        if (!point.inside && !capturing.current) return;
        roomClient.sendRemoteControlMotion({
          t: "m",
          x: point.x,
          y: point.y,
          b: buttonsFromEvent(event),
        });
      }}
      onPointerUp={(event) => {
        capturing.current = false;
        const point = pointFromEvent(event);
        setLocalCursor(event.clientX, event.clientY, point.inside);
        roomClient.sendRemoteControlInput({
          t: "u",
          x: point.x,
          y: point.y,
          b: buttonBitFromEvent(event),
        });
      }}
      onPointerCancel={() => {
        capturing.current = false;
        setLocalCursor(0, 0, false);
      }}
    >
      <div
        ref={cursorRef}
        aria-hidden
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 24,
          height: 24,
          marginTop: -2,
          marginLeft: -2,
          pointerEvents: "none",
          opacity: 0,
          willChange: "transform",
          backgroundImage: `url("data:image/svg+xml,${CURSOR_SVG}")`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "24px 24px",
          filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.45))",
          zIndex: 22,
        }}
      />
    </div>
  );
};

export default RemoteControlSurface;
